<template>
  <div
    data-testid="file-picker-container"
    class="flex items-center gap-6"
    @dragover.prevent="$emit('handleDragOver')"
    @dragleave.prevent="$emit('handleDragLeave')"
    @drop.prevent="handleDrop"
  >
    <label
      class="flex items-center justify-center gap-2 relative cursor-pointer"
      for="fileInput"
    >
      <EncloseIcon />
      <span
        v-if="resolvedfileCounter > 0"
        data-testid="file-picker-badge-counter"
        class="absolute whitespace-nowrap top-0 right-0 h-4 w-4 inline-flex items-center justify-center px-1 py-0.5 text-2xs font-bold leading-none text-base-content transform translate-x-1/2 -translate-y-1/2 bg-white rounded-full"
      >
        {{ resolvedfileCounter }}
      </span>

      <input
        id="fileInput"
        data-testid="file-picker-input"
        type="file"
        name="fileInput"
        :multiple="allowMultiple"
        :disabled="disabled"
        class="hidden"
        @change="uploadFile($event)"
      />
    </label>

    <button
      v-if="showDeleteButton"
      class="flex items-center justify-center gap-2"
      variant="alt"
      :disabled="disabled"
      data-testid="file-picker-delete-button"
      @click="$emit('resetFiles')"
    >
      <DelIcon />
    </button>
  </div>
</template>

<script>
import EncloseIcon from "@/assets/icons/profile-view/enclosure-icon-white.svg";
import DelIcon from "@/assets/icons/svg/delete-icon-white.svg";

export default {
  name: "FilePicker",

  props: {
    /**
     * Amount of files that have been selected/uploaded
     * @type {Number}
     */
    fileCount: {
      type: Number,
      required: true,
    },
    /**
     * Whether the component is in a loading state (disables inputs)
     * @type {Boolean}
     * @default false
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether to show the delete/reset files button
     * @type {Boolean}
     * @default true
     */
    showDeleteButton: {
      type: Boolean,
      default: true,
    },
    /**
     * Whether to allow selecting multiple files at once
     * @type {Boolean}
     * @default true
     */
    allowMultiple: {
      type: Boolean,
      default: true,
    },
  },

  /**
   * Events emitted by this component:
   * 
   * @event uploadFiles - Emitted when files are selected/dropped
   * @param {Object} event - Event object with target.files containing the selected files
   * 
   * @event resetFiles - Emitted when the delete/reset button is clicked
   * 
   * @event handleDragOver - Emitted when files are dragged over the drop zone
   * 
   * @event handleDragLeave - Emitted when files leave the drop zone
   * 
   * @event handleDrop - Emitted after files are successfully dropped
   */

  components: {
    EncloseIcon,
    DelIcon,
  },

  computed: {
    resolvedfileCounter() {
      return this.fileCount ?? 0;
    }
  },

  methods: {
    handleDragOver(event) {
      event.preventDefault();
      this.isDragOver = true;
    },

    handleDragLeave(event) {
      event.preventDefault();
      this.isDragOver = false;
    },

    handleDrop(event) {
      event.preventDefault();
      this.isDragOver = false;

      const { files } = event.dataTransfer ?? {};
      const filesToUpload = !this.allowMultiple && files.length > 1 ? [files[0]] : files;

      this.uploadFile({ target: { files: filesToUpload } });
      this.$emit("handleDrop");
    },

    uploadFile(event) {
      console.log("uploadFile", event);
      const { files } = event.target;
      if (files?.length > 0) {
        this.$emit("uploadFiles", { target: { files } });
      }
    },
  }
}
</script>
